import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { EstensaPrintService } from '../services/angularjs/estensa-print.service';
import { convertBase64ToUint8Array } from '../utils/utils';
import * as pdfjsLib from 'pdfjs-dist';

type PdfModalComponentModal = 'version' | 'pdf' | 'none';
type PdfOrientation = 'portrait' | 'landscape' | 'unknown';

@Component({
    selector: 'se-pdf-modal',
    styleUrls: ['./pdf-modal.component.scss'],
    templateUrl: './pdf-modal.component.html',
    standalone: false
})
export class PdfModalComponent implements OnChanges, OnDestroy {
    @ViewChild('documentModal')
    documentModal: TemplateRef<any>;

    @Input()
    visible = false;

    @Input()
    sourceBase64: string;

    @Input()
    print: boolean;

    @Output()
    busy = new EventEmitter<boolean>(true);

    @Output()
    visibleChange = new EventEmitter<boolean>(true);

    @Output()
    documentIdChange = new EventEmitter<string>();

    @Output()
    ecgDocIdArrayChange = new EventEmitter<string[]>();

    activeDialog: MatDialogRef<any>;

    activeDialogName: PdfModalComponentModal = 'none';

    afterCloseEventSubscription: Subscription;

    zoom = 1.2;

    source: Uint8Array;

    pdfOrientation: PdfOrientation = 'unknown';

    constructor(
        public dialog: MatDialog,
        private printService: EstensaPrintService,
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.visible && !changes.visible.currentValue) {
            this.closeDialog();
        } else if (changes.visible && changes.visible.currentValue) {
            this.selectSource();
        }
    }

    ngOnDestroy() {
        this.afterCloseEventSubscription?.unsubscribe();
    }

    closeDialog() {
        if (this.activeDialog && this.activeDialog.getState() == MatDialogState.OPEN) {
            this.activeDialog.close();
        }
    }

    onPrint() {
        this.printService.print(convertBase64ToUint8Array(this.sourceBase64));
    }

    zoomIn() {
        this.zoom += 0.1;
    }

    zoomOut() {
        if (this.zoom > 0.1) {
            this.zoom -= 0.1;
        }
    }

    private async selectSource() {
        if (!this.sourceBase64) {
            return;
        }

        this.source = convertBase64ToUint8Array(this.sourceBase64);

        // NEGRAR: Detect PDF orientation to adapt window dimensions
        try {
            await this.detectPdfOrientation();
        } catch (error) {
            console.warn('Unable to detect PDF orientation, using landscape as default', error);
            this.pdfOrientation = 'landscape';
        }

        // NEGRAR: Adjust initial zoom based on orientation
        this.zoom = this.pdfOrientation === 'portrait' ? 0.9 : 1.0;

        if (this.print) {
            this.openPrintDialog();
        } else {
            this.openDialog();
        }
    }

    /**
     * Detects the PDF orientation by analyzing the first page
     */
    private async detectPdfOrientation(): Promise<void> {
        try {
            // NEGRAR: Configure the worker for pdfjs-dist if not already configured
            if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
                pdfjsLib.GlobalWorkerOptions.workerSrc = (window['pdfWorkerSrc'] || '/pdf.worker.min.mjs');
            }
            
            // Use a copy of the source to avoid modifying the original
            const sourceCopy = new Uint8Array(this.source);
            const loadingTask = pdfjsLib.getDocument({ data: sourceCopy });
            const pdf = await loadingTask.promise;
            
            if (pdf.numPages > 0) {
                const page = await pdf.getPage(1);
                const viewport = page.getViewport({ scale: 1 });
                
                // Determine orientation based on dimensions
                // If width is greater than height, it's landscape
                // Otherwise it's portrait
                if (viewport.width > viewport.height) {
                    this.pdfOrientation = 'landscape';
                } else {
                    this.pdfOrientation = 'portrait';
                }
                
            }
        } catch (error) {
            console.warn('Unable to detect PDF orientation, using landscape as default', error);
            this.pdfOrientation = 'landscape';
        }
    }

    private openPrintDialog() {
        try {
            this.printService.print(this.source);
        } finally {
            this.source = null;
            this.visibleChange.emit(false);
            this.documentIdChange.emit('');
            this.ecgDocIdArrayChange.emit([]);
        }
    }

    private openDialog() {
        const panelClass = this.pdfOrientation === 'portrait' 
            ? 'pdf-modal-portrait' 
            : 'pdf-modal-landscape';

        this.activeDialog = this.dialog.open(this.documentModal, {
            autoFocus: false,
            panelClass: panelClass,
        });

        this.afterCloseEventSubscription = this.activeDialog.afterClosed().subscribe(() => {
            this.source = null;
            this.visibleChange.emit(false);
            this.documentIdChange.emit('');
            this.ecgDocIdArrayChange.emit([]);

            this.afterCloseEventSubscription.unsubscribe();
        });
    }
}
